import { useMutation } from '@apollo/client';
import React from 'react';
import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import {
  initialSelectedNewsLetter,
  reducerResetSelectedNewsLetter,
  reducerUpdateActiveSection
} from 'src/features/customNewsLetters';
import { useAlert } from 'src/hooks/useAlert';
import {
  FrequentyDelivery,
  ICreateDataSourceBroadcastMediaPayload,
  ICreateDataSourceOnmPayload,
  ICreateDataSourcePrintMediaPayload,
  ICreateDataSourceSosmedPayload,
  INewsLetterAddAutomaticPayload,
  INewsLetterAddAutomaticResponse,
  INewsLetterAddPayload,
  INewsLetterAddResponse,
  INewsLetterDetailArgument,
  INewsLetterEditAutomaticPayload,
  INewsLetterEditDetailVariables,
  INewsLetterEditPayload
} from 'src/models/newsLetter';
import { CUSTOM_REPORT } from 'src/route';
import {
  ADD_NEWS_LETTER,
  ADD_NEWS_LETTER_AUTOMATIC,
  CREATE_DATA_SOURCE_BROADCAST,
  CREATE_DATA_SOURCE_ONM,
  CREATE_DATA_SOURCE_PRINT_MEDIA,
  CREATE_DATA_SOURCE_SOSMED,
  EDIT_NEWSLETTER,
  EDIT_NEWSLETTER_AUTOMATIC,
  EDIT_NEWSLETTER_DETAIL
} from 'src/services/graphql/customNewsLetter/mutation';
import { EmailSettingFormValues } from '../components/FormNewsletterManual/StepEmailSetting';
import { EmailSettingFormValues as EmailSettingFormValuesAutomatic } from '../components/FormNewsletterAutomatic/StepEmailSetting';
import countKeywordsInStream from '../utils/countKeywordInStream';
import { IGranularity } from 'src/models/general';
import {
  initialState as initialStateSosmed,
  reducerUpdateAdvanceSearch
} from 'src/features/advanceSearchSocialMedia';
import {
  initialState as initialStateOfm,
  reducerUpdateAdvanceSearchOfm
} from 'src/features/advanceSearchOfflineMedia';
import {
  initialState as initialStateOnm,
  reducerUpdateAdvanceSearchOnm
} from 'src/features/advanceSearchOnlineMedia';

const useActionNewsLetter = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { handleClickAlert } = useAlert();

  const { id: projectId, timezone } = useAppSelector(
    (state) => state.users.project
  );

  const { customizeTemplate, selectDataSource, emailSetting } = useAppSelector(
    (state) => state.storeCustomNewsLetter.selectedNewsLetter
  );

  const handleResetPayload = () => {
    dispatch(reducerUpdateActiveSection(''));
    dispatch(reducerResetSelectedNewsLetter(initialSelectedNewsLetter));
    dispatch(reducerUpdateAdvanceSearchOnm(initialStateOnm));
    dispatch(reducerUpdateAdvanceSearchOfm(initialStateOfm));
    dispatch(reducerUpdateAdvanceSearch(initialStateSosmed));
  };

  async function safelyRunMutation(
    fn: () => Promise<any>,
    errorMessage: string
  ) {
    try {
      await fn();
    } catch (error) {
      handleClickAlert({
        message: errorMessage,
        severity: 'error',
        vertical: 'top',
        horizontal: 'center'
      });
      throw error;
    }
  }

  /**
   * Add news letter
   */

  const payloadNewsLetter = React.useMemo(() => {
    return {
      projectId: projectId,
      titleColor: customizeTemplate.titleColor,
      readMoreLabel: !customizeTemplate.isSeedMoreButton
        ? ''
        : customizeTemplate.readMoreLabel,
      fontColor: customizeTemplate.fontColor,
      isLike: customizeTemplate.isLikeMetric,
      isView: customizeTemplate.isViewMetric,
      isComment: customizeTemplate.isCommentMetric,
      isEngagement: customizeTemplate.isEngagementMetric,
      isSentimenSocialMedia: customizeTemplate.isSentimentMetric,
      isPrValue: customizeTemplate.isPrValue,
      isKeyWord: customizeTemplate.isNumberOfKeywords,
      isSentiment: customizeTemplate.isSentiment,
      colorHeaderSection: customizeTemplate.mainColor,
      footerBackgroundColor: customizeTemplate.footerBackgroudColor,
      ...(customizeTemplate.logoUrl &&
        customizeTemplate.isLogo && {
          logoUrl: customizeTemplate.logoUrl
        }),
      ...(customizeTemplate.headerImageUrl &&
        customizeTemplate.isHeaderImage && {
          headerImageUrl: customizeTemplate.headerImageUrl
        }),
      ...(customizeTemplate.primaryText &&
        customizeTemplate.isPrimaryText && {
          primaryText: customizeTemplate.primaryText
        }),
      ...(customizeTemplate.secondaryText &&
        customizeTemplate.isSecondaryText && {
          secondaryText: customizeTemplate.secondaryText
        }),
      ...(customizeTemplate.footerText && {
        footerText: customizeTemplate.footerText
      }),
      ...(customizeTemplate.instagram &&
        customizeTemplate.isFooterSocialMedia && {
          instagramUrl: customizeTemplate.instagram
        }),
      ...(customizeTemplate.twitter &&
        customizeTemplate.isFooterSocialMedia && {
          twitterUrl: customizeTemplate.twitter
        }),
      ...(customizeTemplate.tiktok &&
        customizeTemplate.isFooterSocialMedia && {
          tiktokUrl: customizeTemplate.tiktok
        }),
      ...(customizeTemplate.youtube &&
        customizeTemplate.isFooterSocialMedia && {
          youtubeUrl: customizeTemplate.youtube
        })
    };
  }, [customizeTemplate, emailSetting]);

  const [createNewsLetter, resCreateNewsLetter] = useMutation<
    INewsLetterAddResponse,
    INewsLetterAddPayload
  >(ADD_NEWS_LETTER, {
    onCompleted: async (data) => {
      try {
        const newsLetterId = data.newsLetter_addNewsLetter;
        const newsLetterDetailArgument = [
          ...selectDataSource.selectedStreamSosmedList.map((item) => ({
            ...item,
            source: 'socialMedia',
            projectId: projectId,
            newsLetterUuid: newsLetterId
          })),
          ...selectDataSource.selectedStreamPrintMediaList.map((item) => ({
            ...item,
            source: 'print',
            projectId: projectId,
            newsLetterUuid: newsLetterId
          })),
          ...selectDataSource.selectedStreamOnlineMediaList.map((item) => ({
            ...item,
            source: 'onlineNews',
            projectId: projectId,
            newsLetterUuid: newsLetterId
          })),
          ...selectDataSource.selectedStreamBroadcastList.map((item) => ({
            ...item,
            source: 'broadcast',
            projectId: projectId,
            newsLetterUuid: newsLetterId
          }))
        ] as INewsLetterDetailArgument[];

        newsLetterId &&
          (await safelyRunMutation(
            () =>
              updateStreamNewsLetter({
                variables: {
                  newsLetterId: newsLetterId,
                  projectId: projectId,
                  newsLetterDetailArgument: newsLetterDetailArgument
                }
              }),
            'Failed to save broadcast data source.'
          ));

        // Semua sukses
        handleClickAlert({
          message: 'Your edit has been saved.',
          severity: 'success',
          vertical: 'top',
          horizontal: 'center'
        });

        handleResetPayload();
        navigate(`${CUSTOM_REPORT}?tab=news_letter`);
      } catch (error) {
        console.error('Error creating newsletter chain:', error);
        handleClickAlert({
          message: 'Error creating newsletter chain: ' + error.message,
          severity: 'error',
          vertical: 'top',
          horizontal: 'center'
        });
      }
    },
    onError: () => {
      handleClickAlert({
        message: 'Failed to save your edit.',
        severity: 'error',
        vertical: 'top',
        horizontal: 'center'
      });
    }
  });

  const clearingKeyword = (keyword: string, stream: string): string[] => {
    const keywords = keyword.split(',').map((item) => item.trim());
    const keywordWithoutCount = keywords.map((keyWithCount) => {
      const splitKeyword = keyWithCount.split(':');
      return splitKeyword[0].trim();
    });

    return countKeywordsInStream(stream, keywordWithoutCount);
  };

  /**
   * edit news letter
   */
  const [updateNewsLetter, resUpdateNewsLetter] = useMutation<
    { newsLetter_editNewsLetter: string },
    INewsLetterEditPayload
  >(EDIT_NEWSLETTER, {
    onCompleted: async (data) => {
      try {
        const newsLetterId = data?.newsLetter_editNewsLetter;

        const newsLetterDetailArgument = [
          ...selectDataSource.selectedStreamSosmedList.map((item) => ({
            ...item,
            source: 'socialMedia',
            projectId: projectId,
            newsLetterUuid: newsLetterId,
            prValue: item.prValue ? String(item.prValue) : '',
            keywords: item.keywords
              ? clearingKeyword(item.keywords, item.content).join(',')
              : ''
          })),
          ...selectDataSource.selectedStreamPrintMediaList.map((item) => ({
            ...item,
            source: 'print',
            projectId: projectId,
            newsLetterUuid: newsLetterId,
            keywords: item.keywords
              ? clearingKeyword(item.keywords, item.content).join(',')
              : ''
          })),
          ...selectDataSource.selectedStreamOnlineMediaList.map((item) => ({
            ...item,
            source: 'onlineNews',
            projectId: projectId,
            newsLetterUuid: newsLetterId,
            keywords: item.keywords
              ? clearingKeyword(item.keywords, item.content).join(',')
              : ''
          })),
          ...selectDataSource.selectedStreamBroadcastList.map((item) => ({
            ...item,
            source: 'broadcast',
            projectId: projectId,
            newsLetterUuid: newsLetterId,
            keywords: item.keywords
              ? clearingKeyword(item.keywords, item.content).join(',')
              : ''
          }))
        ] as INewsLetterDetailArgument[];

        newsLetterId &&
          (await safelyRunMutation(
            () =>
              updateStreamNewsLetter({
                variables: {
                  newsLetterId: newsLetterId,
                  projectId: projectId,
                  newsLetterDetailArgument: newsLetterDetailArgument
                }
              }),
            'Failed to save streams data'
          ));

        handleResetPayload();
      } catch (error) {
        console.error('Error creating newsletter chain:', error);
        handleClickAlert({
          message: 'Error creating newsletter chain: ' + error.message,
          severity: 'error',
          vertical: 'top',
          horizontal: 'center'
        });
      }
    },
    onError: () => {
      handleClickAlert({
        message: 'Failed to save setting your edit.',
        severity: 'error',
        vertical: 'top',
        horizontal: 'center'
      });
    }
  });

  /**
   * edit stream news letter
   */
  const [updateStreamNewsLetter, resStreamNewsLetter] = useMutation<
    {
      newsLetter_editNewsLetterDetail: string;
    },
    INewsLetterEditDetailVariables
  >(EDIT_NEWSLETTER_DETAIL, {
    onCompleted: () => {
      dispatch(reducerResetSelectedNewsLetter(initialSelectedNewsLetter));
      setTimeout(() => {
        navigate(`${CUSTOM_REPORT}?tab=news_letter`);
      }, 4000);

      handleClickAlert({
        message: 'Your edit has been saved.',
        severity: 'success',
        vertical: 'top',
        horizontal: 'center'
      });
    },
    onError: () => {
      handleClickAlert({
        message: 'Failed to save stream your edit.',
        severity: 'error',
        vertical: 'top',
        horizontal: 'center'
      });
    }
  });

  //action
  const handleCreateNewsLetter = async (value: EmailSettingFormValues) => {
    console.log('value', value);
    await createNewsLetter({
      variables: {
        ...payloadNewsLetter,
        templateName: value.templateName,
        emailSubject: value.emailSubject,
        recepientEmail: value.recipient,
        metric: value.metric as FrequentyDelivery,
        timezone: timezone,
        deliveryDateTime: value.deliveryDate
      }
    });
  };

  const handleUpdateNewsLetter = async (
    newsLetterId: string,
    value: EmailSettingFormValues
  ) => {
    try {
      console.log(value, 'value');

      await updateNewsLetter({
        variables: {
          projectId: projectId,
          newsLetterId: newsLetterId,
          templateName: value.templateName,
          emailSubject: value.emailSubject,
          recepientEmail: value.recipient,
          metric: value.metric as FrequentyDelivery,
          timezone: timezone,
          deliveryDateTime: value.deliveryDate,
          ...payloadNewsLetter
        }
      });

      handleClickAlert({
        message: 'Your edit has been saved.',
        severity: 'success',
        vertical: 'top',
        horizontal: 'center'
      });
    } catch (error) {
      handleClickAlert({
        message: 'Failed to save stream your edit.',
        severity: 'error',
        vertical: 'top',
        horizontal: 'center'
      });
    }
  };

  const isValidRowStream = React.useMemo(() => {
    if (
      selectDataSource?.selectedStreamBroadcastList.length <= 0 &&
      selectDataSource?.selectedStreamOnlineMediaList?.length <= 0 &&
      selectDataSource?.selectedStreamPrintMediaList?.length <= 0 &&
      selectDataSource?.selectedStreamSosmedList?.length <= 0
    ) {
      return 'Please add at least one row';
    }
    return '';
  }, [
    selectDataSource?.selectedStreamBroadcastList.length,
    selectDataSource?.selectedStreamOnlineMediaList?.length,
    selectDataSource?.selectedStreamPrintMediaList?.length,
    selectDataSource?.selectedStreamSosmedList?.length
  ]);

  /**
   * Action newsletter with schedule
   */
  const { groupObject } = useAppSelector((store) => store.objectManagement);

  const objectIdList = React.useMemo(() => {
    if (selectDataSource.dataSource === 'group') {
      const group = groupObject.data.find(
        (group) => group.id === selectDataSource.objectGroupId
      );
      return (group?.objectList || []).map((object) => object.id);
    }
    return selectDataSource.objectIds;
  }, [
    selectDataSource.objectIds,
    selectDataSource.dataSource,
    groupObject,
    selectDataSource.objectGroupId
  ]);

  const { languageSelected } = useAppSelector(
    (state) => state.storeOfflineMediaStream
  );

  const granularity: IGranularity = {
    unit: 'day',
    value: 1
  };

  const {
    filterDataSourceOnm,
    filterDataSourceSosmed,
    filterDataSourceBroadcast,
    filterDataSourcePrintMedia
  } = selectDataSource;

  const payloadDataSource = {
    languageList: [],
    projectId,
    start: new Date(selectDataSource.startDate),
    end: new Date(selectDataSource.endDate),
    granularity,
    timezone: timezone
  };

  const [createNewsLetterAuto, resCreateNewsLetterAuto] = useMutation<
    INewsLetterAddAutomaticResponse,
    INewsLetterAddAutomaticPayload
  >(ADD_NEWS_LETTER_AUTOMATIC, {
    onCompleted: async (data) => {
      try {
        const newsLetterId = data?.newsLetter_addNewsLetterAutomatic;
        if (!newsLetterId) {
          return handleClickAlert({
            message: 'Failed to save newsletter.',
            severity: 'error',
            vertical: 'top',
            horizontal: 'center'
          });
        }

        // PAYLOADS
        const payloadOnm: ICreateDataSourceOnmPayload = {
          ...payloadDataSource,
          ...filterDataSourceOnm,
          clippingId: selectDataSource.clippingId,
          newsLetterAutomaticId: newsLetterId
        };

        const payloadSosmed: ICreateDataSourceSosmedPayload = {
          ...payloadDataSource,
          ...filterDataSourceSosmed,
          objectList: objectIdList,
          newsLetterAutomaticId: newsLetterId
        };

        const payloadPrintMedia: ICreateDataSourcePrintMediaPayload = {
          ...payloadDataSource,
          ...filterDataSourcePrintMedia,
          languageList:
            languageSelected === 'all-language' ? [] : [languageSelected],
          clippingId: selectDataSource.printMediaId,
          newsLetterAutomaticId: newsLetterId
        };

        const payloadBroadcast: ICreateDataSourceBroadcastMediaPayload = {
          ...payloadDataSource,
          ...filterDataSourceBroadcast,
          languageList:
            languageSelected === 'all-language' ? [] : [languageSelected],
          clippingId: selectDataSource.broadcastId,
          newsLetterAutomaticId: newsLetterId
        };

        // SEQUENTIAL EXECUTION DENGAN HANDLING
        selectDataSource.clippingId &&
          (await safelyRunMutation(
            () => createDataSourceOnm({ variables: payloadOnm }),
            'Failed to save online news data source.'
          ));

        objectIdList.length > 0 &&
          (await safelyRunMutation(
            () => createDataSourceSosmed({ variables: payloadSosmed }),
            'Failed to save social media data source.'
          ));

        selectDataSource.printMediaId &&
          (await safelyRunMutation(
            () => createDataSourcePrintMedia({ variables: payloadPrintMedia }),
            'Failed to save print media data source.'
          ));

        selectDataSource.broadcastId &&
          (await safelyRunMutation(
            () => createDataSourceBroadcast({ variables: payloadBroadcast }),
            'Failed to save broadcast data source.'
          ));

        // Semua sukses
        handleClickAlert({
          message: 'Your edit has been saved.',
          severity: 'success',
          vertical: 'top',
          horizontal: 'center'
        });

        handleResetPayload();
        navigate(`${CUSTOM_REPORT}?tab=news_letter`);
      } catch (error) {
        console.error('Error creating newsletter chain:', error);
        handleClickAlert({
          message: 'Error creating newsletter chain: ' + error.message,
          severity: 'error',
          vertical: 'top',
          horizontal: 'center'
        });
      }
    },
    onError: () => {
      handleClickAlert({
        message: 'Failed to save your edit.',
        severity: 'error',
        vertical: 'top',
        horizontal: 'center'
      });
    }
  });

  const handleCreateNewsLetterAuto = async (
    value: EmailSettingFormValuesAutomatic
  ) => {
    console.log('value', value);
    await createNewsLetterAuto({
      variables: {
        ...payloadNewsLetter,
        templateName: value.templateName,
        emailSubject: value.emailSubject,
        recepientEmail: value.recipient,
        metric: value.metric as FrequentyDelivery,
        timezone: timezone,
        deliveryDateTime: value.deliveryDate,
        newsLetterStart: value.newsLetterStart
      }
    });
  };

  const [createDataSourceSosmed, resCreateDataSourceSosmed] = useMutation<
    { newsLetter_createDataSourceOnlineNews: string },
    ICreateDataSourceSosmedPayload
  >(CREATE_DATA_SOURCE_SOSMED, {
    onCompleted: () => {
      handleClickAlert({
        message: 'Your edit has been saved.',
        severity: 'success',
        vertical: 'top',
        horizontal: 'center'
      });
    },
    onError: () => {
      handleClickAlert({
        message: 'Failed to save social media data source.',
        severity: 'error',
        vertical: 'top',
        horizontal: 'center'
      });
    }
  });

  //onm
  const [createDataSourceOnm, resCreateDataSourceOnm] = useMutation<
    { newsLetter_createDataSourceOnlineNews: string },
    ICreateDataSourceOnmPayload
  >(CREATE_DATA_SOURCE_ONM, {
    onCompleted: () => {
      handleClickAlert({
        message: 'Your edit has been saved.',
        severity: 'success',
        vertical: 'top',
        horizontal: 'center'
      });
    },
    onError: () => {
      handleClickAlert({
        message: 'Failed to save online news data source.',
        severity: 'error',
        vertical: 'top',
        horizontal: 'center'
      });
    }
  });

  //print media
  const [createDataSourcePrintMedia, resCreateDataSourcePrintMedia] =
    useMutation<
      { newsLetter_createDataSourcePrint: string },
      ICreateDataSourcePrintMediaPayload
    >(CREATE_DATA_SOURCE_PRINT_MEDIA, {
      onCompleted: () => {
        handleClickAlert({
          message: 'Your edit has been saved.',
          severity: 'success',
          vertical: 'top',
          horizontal: 'center'
        });
      },
      onError: () => {
        handleClickAlert({
          message: 'Failed to save print media data source.',
          severity: 'error',
          vertical: 'top',
          horizontal: 'center'
        });
      }
    });

  //print media
  const [createDataSourceBroadcast, resCreateDataSourceBroadcast] = useMutation<
    { newsLetter_createDataSourceBroadcast: string },
    ICreateDataSourceBroadcastMediaPayload
  >(CREATE_DATA_SOURCE_BROADCAST, {
    onCompleted: () => {
      handleClickAlert({
        message: 'Your edit has been saved.',
        severity: 'success',
        vertical: 'top',
        horizontal: 'center'
      });
    },
    onError: () => {
      handleClickAlert({
        message: 'Failed to save broadcast data source.',
        severity: 'error',
        vertical: 'top',
        horizontal: 'center'
      });
    }
  });

  const [updateNewsLetterAuto, resUpdateNewsLetterAuto] = useMutation<
    { newsLetter_editNewsLetterAutomatic: string },
    INewsLetterEditAutomaticPayload
  >(EDIT_NEWSLETTER_AUTOMATIC, {
    onCompleted: async (data) => {
      try {
        const newsLetterId = data?.newsLetter_editNewsLetterAutomatic;
        if (!newsLetterId) {
          return handleClickAlert({
            message: 'Failed to save newsletter.',
            severity: 'error',
            vertical: 'top',
            horizontal: 'center'
          });
        }

        // PAYLOADS
        const payloadOnm: ICreateDataSourceOnmPayload = {
          ...payloadDataSource,
          ...filterDataSourceOnm,
          clippingId:
            selectDataSource.clippingId ||
            '00000000-0000-0000-0000-000000000abc',
          newsLetterAutomaticId: newsLetterId
        };

        const payloadSosmed: ICreateDataSourceSosmedPayload = {
          ...payloadDataSource,
          ...filterDataSourceSosmed,
          objectList:
            objectIdList.length > 0
              ? objectIdList
              : ['00000000-0000-0000-0000-000000000abc'],
          newsLetterAutomaticId: newsLetterId
        };

        const payloadPrintMedia: ICreateDataSourcePrintMediaPayload = {
          ...payloadDataSource,
          ...filterDataSourcePrintMedia,
          languageList:
            languageSelected === 'all-language' ? [] : [languageSelected],
          clippingId:
            selectDataSource.printMediaId ||
            '00000000-0000-0000-0000-000000000abc',
          newsLetterAutomaticId: newsLetterId
        };

        const payloadBroadcast: ICreateDataSourceBroadcastMediaPayload = {
          ...payloadDataSource,
          ...filterDataSourceBroadcast,
          languageList:
            languageSelected === 'all-language' ? [] : [languageSelected],
          clippingId:
            selectDataSource.broadcastId ||
            '00000000-0000-0000-0000-000000000abc',
          newsLetterAutomaticId: newsLetterId
        };

        // SEQUENTIAL EXECUTION DENGAN HANDLING
        // selectDataSource.clippingId &&
        //   (await safelyRunMutation(
        //     () => createDataSourceOnm({ variables: payloadOnm }),
        //     'Failed to save online news data source.'
        //   ));
        await safelyRunMutation(
          () => createDataSourceOnm({ variables: payloadOnm }),
          'Failed to save online news data source.'
        );

        // objectIdList.length > 0 &&
        //   (await safelyRunMutation(
        //     () => createDataSourceSosmed({ variables: payloadSosmed }),
        //     'Failed to save social media data source.'
        //   ));
        await safelyRunMutation(
          () => createDataSourceSosmed({ variables: payloadSosmed }),
          'Failed to save social media data source.'
        );

        // selectDataSource.printMediaId &&
        //   (await safelyRunMutation(
        //     () => createDataSourcePrintMedia({ variables: payloadPrintMedia }),
        //     'Failed to save print media data source.'
        //   ));
        await safelyRunMutation(
          () => createDataSourcePrintMedia({ variables: payloadPrintMedia }),
          'Failed to save print media data source.'
        );

        // selectDataSource.broadcastId &&
        //   (await safelyRunMutation(
        //     () => createDataSourceBroadcast({ variables: payloadBroadcast }),
        //     'Failed to save broadcast data source.'
        //   ));
        await safelyRunMutation(
          () => createDataSourceBroadcast({ variables: payloadBroadcast }),
          'Failed to save broadcast data source.'
        );

        // Semua sukses
        handleClickAlert({
          message: 'Your edit has been saved.',
          severity: 'success',
          vertical: 'top',
          horizontal: 'center'
        });

        handleResetPayload();
        navigate(`${CUSTOM_REPORT}?tab=news_letter`);
      } catch (error) {
        console.error('Error creating newsletter chain:', error);
        handleClickAlert({
          message: 'Error creating newsletter chain: ' + error.message,
          severity: 'error',
          vertical: 'top',
          horizontal: 'center'
        });
      }
    },
    onError: () => {
      handleClickAlert({
        message: 'Failed to save setting your edit.',
        severity: 'error',
        vertical: 'top',
        horizontal: 'center'
      });
    }
  });

  const handleUpdateNewsLetterAuto = async (
    newsLetterAutomaticId: string,
    value: EmailSettingFormValuesAutomatic
  ) => {
    try {
      console.log(value, 'value');

      await updateNewsLetterAuto({
        variables: {
          projectId: projectId,
          newsLetterAutomaticId: newsLetterAutomaticId,
          templateName: value.templateName,
          emailSubject: value.emailSubject,
          recepientEmail: value.recipient,
          metric: value.metric as FrequentyDelivery,
          timezone: timezone,
          deliveryDateTime: value.deliveryDate,
          newsLetterStart: value.newsLetterStart,
          ...payloadNewsLetter
        }
      });

      handleClickAlert({
        message: 'Your edit has been saved.',
        severity: 'success',
        vertical: 'top',
        horizontal: 'center'
      });
    } catch (error) {
      handleClickAlert({
        message: 'Failed to save stream your edit.',
        severity: 'error',
        vertical: 'top',
        horizontal: 'center'
      });
    }
  };

  const isValidRowSchaduleTime = React.useMemo(() => {
    if (
      emailSetting.newsLetterStart &&
      emailSetting.newsLetterStart.length < 1
    ) {
      return 'Please add at least one schedule time';
    }
    return '';
  }, [emailSetting.newsLetterStart.length]);

  return {
    // add news letter
    createNewsLetter,
    resCreateNewsLetter: resCreateNewsLetter,

    // add news letter auto
    createNewsLetterAuto,
    resCreateNewsLetterAuto: resCreateNewsLetterAuto,

    // edit news letter
    updateNewsLetter,
    resUpdateNewsLetter: resUpdateNewsLetter,

    // edit news letter auto
    updateNewsLetterAuto,
    resUpdateNewsLetterAuto: resUpdateNewsLetterAuto,

    // edit stream news letter
    updateStreamNewsLetter,
    resStreamNewsLetter: resStreamNewsLetter,

    // action
    handleCreateNewsLetter,
    handleUpdateNewsLetter,
    handleCreateNewsLetterAuto,
    handleUpdateNewsLetterAuto,

    //validation total row stream
    isValidRowStream,
    isValidRowSchaduleTime,

    //reset payload
    handleResetPayload
  };
};

export default useActionNewsLetter;
